import { useState, useEffect, lazy, Suspense } from 'react';
import LandingPage from './components/LandingPage';
import Toasts from './components/Toasts';
import Modals from './components/Modals';
import { UserProfile } from './components/SignInModal';
import { ToastMessage } from './types';
import { injectSEO, SEO_MAP } from './utils/seo';
import { supabase } from './utils/supabaseClient';
import { getDeviceFingerprint } from './utils/fingerprint';
import { getSharedCookie, setSharedCookie, eraseSharedCookie } from './utils/cookieHelper';


import SpecialPages from './components/SpecialPages';
const DashboardTool = lazy(() => import('./components/DashboardTool'));
const SignInModal = lazy(() => import('./components/SignInModal'));
const CelebrationModal = lazy(() => import('./components/CelebrationModal'));
const AdminPortal = lazy(() => import('./components/admin/AdminPortal'));

// Obfuscated Admin Control Panel path decoders
const _s = [0x53, 0x49];
const _d = (e: number[]): string => e.map((c, i) => String.fromCharCode(c ^ _s[i % _s.length])).join('');
const ADMIN_PATH = _d([124,32,62,40,52,32,53,48,126,58,54,42,38,59,54,100,50,42,48,44,32,58,126,57,50,39,54,37,126,112,107,126,97]);

const LoadingFallback = () => (
  <div className="fixed inset-0 bg-[#09090b] flex items-center justify-center z-50">
    <div className="w-8 h-8 rounded-full border-2 border-white/5 border-t-[#aace16] animate-spin" />
  </div>
);

const DashboardLoader = ({ theme }: { theme: 'dark' | 'light' }) => (
  <div className={`fixed inset-0 flex items-center justify-center z-[300] animate-fade-out transition-colors duration-300 ${theme === 'light' ? 'bg-[#f4f4f5]' : 'bg-[#09090b]'}`} style={{ animationDelay: '1.2s', animationDuration: '0.3s', animationFillMode: 'forwards' }}>
    <div className="relative flex items-center justify-center">
      <div className={`absolute w-20 h-20 rounded-full border-[3px] border-t-[#aace16] animate-spin transition-colors duration-300 ${theme === 'light' ? 'border-zinc-300' : 'border-white/5'}`} style={{ animationDuration: '1s' }} />
      <img 
        src={theme === 'light' ? "/Logo/logo white mode.png" : "/Logo/logo dark mode.png"} 
        alt="Loading" 
        width="32" 
        height="32" 
        className="w-8 h-8 object-contain" 
      />
    </div>
  </div>
);

export default function App() {
  const [currentPath, setCurrentPath] = useState(() => {
    let path = window.location.pathname;
    // Redirect bulk-image-optimizer alias immediately
    if (path === '/bulk-image-optimizer') {
      path = '/bulk-image-compressor';
      window.history.replaceState(null, '', path);
    }
    if (path === '/about-us') {
      path = '/about';
      window.history.replaceState(null, '', path);
    }
    if (path === '/contact-us') {
      path = '/contact';
      window.history.replaceState(null, '', path);
    }
    return path;
  });

  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    try {
      const stored = localStorage.getItem('si_user_profile');
      if (stored) return JSON.parse(stored);
      
      const cookieProfile = getSharedCookie('si_user_profile');
      if (cookieProfile) {
        const parsed = JSON.parse(cookieProfile);
        localStorage.setItem('si_user_profile', JSON.stringify(parsed));
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  });

  const [currentView, setCurrentView] = useState<'landing' | 'app' | 'special' | 'admin'>(() => {
    const isAppSubdomain = window.location.hostname.includes('app.');
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    let path = window.location.pathname;
    if (path === '/dashboard' && isAppSubdomain && !isLocalhost) {
      path = '/';
      try {
        window.history.replaceState(null, '', '/');
      } catch {}
    }
    if (path === '/about-us') path = '/about';
    if (path === '/contact-us') path = '/contact';

    let hasProfile = false;
    let isGuest = false;
    try {
      const stored = localStorage.getItem('si_user_profile') || getSharedCookie('si_user_profile');
      if (stored) {
        hasProfile = true;
        const parsed = typeof stored === 'string' ? JSON.parse(stored) : stored;
        if (parsed.email === 'guest@smartimagify.com') {
          isGuest = true;
        }
      }
    } catch {}

    const toolPaths = [
      '/dashboard', '/compress-image', '/bulk-image-compressor', '/ai-image-generator',
      '/image-resizer', '/image-format-converter', '/stock-library',
      '/account', '/settings', '/referral', '/history', '/donate', '/collage-maker', '/passport-photo-maker'
    ];
    const isToolPath = toolPaths.includes(path) || ((path === '/pricing' || path === '/about' || path === '/about-us') && hasProfile);

    const specialPaths = [
      '/privacy-policy', '/terms-of-service', '/disclaimer', '/cookie-policy', '/faq',
      '/sitemap', '/about', '/about-us', '/blog', '/contact', '/contact-us', '/tools', '/dashboard-preview',
      '/background-remover', '/image-upscaler', '/watermark-remover', '/image-to-pdf',
      '/pdf-to-image', '/compress-gif', '/image-color-picker', '/exif-remover',
      '/pricing'
    ];
    const isSpecialPath = (specialPaths.includes(path) && !(hasProfile && (path === '/pricing' || path === '/about' || path === '/about-us'))) || (!hasProfile && !isAppSubdomain && toolPaths.includes(path) && path !== '/dashboard');

    if (path === ADMIN_PATH) return 'admin';

    if (isSpecialPath) return 'special';

    // Enforce subdomain separation in production:
    // If an authenticated user accesses a tool path on the naked domain, redirect to the app subdomain!
    if (!isAppSubdomain && !isLocalhost && isToolPath && hasProfile) {
      window.location.href = `https://app.smartimagify.com${path}`;
      return 'landing';
    }

    if (path === '/') {
      if (isAppSubdomain && hasProfile) return 'app';
      return 'landing';
    }

    if (isAppSubdomain) return 'app';
    if (isToolPath) return 'app';

    return 'landing';
  });

  // Trigger popups loaders
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Sign-In & Celebration modal state
  const [showSignIn, setShowSignIn] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [pendingPlanActivation, setPendingPlanActivation] = useState<string | null>(null);
  
  // pendingTab will store the clean route path to redirect to after successful sign-in
  const [pendingTabPath, setPendingTabPath] = useState<string | null>(null);
  const [signInMode, setSignInMode] = useState<'signin' | 'signup'>('signin');


  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      const stored = localStorage.getItem('i_settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.theme === 'light') return 'light';
      }
    } catch {}
    return 'dark';
  });

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    
    // Disable transitions/animations temporarily
    document.documentElement.classList.add('theme-toggling');
    
    setTheme(nextTheme);
    if (nextTheme === 'light') {
      document.body.classList.add('light');
    } else {
      document.body.classList.remove('light');
    }
    try {
      const stored = localStorage.getItem('i_settings');
      let currentSettings = {};
      if (stored) {
        currentSettings = JSON.parse(stored);
      }
      const updatedSettings = { ...currentSettings, theme: nextTheme };
      localStorage.setItem('i_settings', JSON.stringify(updatedSettings));
    } catch (e) {
      console.error(e);
    }
    
    setTimeout(() => {
      document.documentElement.classList.remove('theme-toggling');
    }, 100);
  };

  useEffect(() => {
    document.documentElement.classList.add('theme-toggling');
    if (theme === 'light') {
      document.body.classList.add('light');
    } else {
      document.body.classList.remove('light');
    }
    const timer = setTimeout(() => {
      document.documentElement.classList.remove('theme-toggling');
    }, 100);
    return () => clearTimeout(timer);
  }, [theme]);

  // Keep shared subdomain cookie in sync with userProfile state
  useEffect(() => {
    if (userProfile) {
      setSharedCookie('si_user_profile', JSON.stringify(userProfile));
    } else {
      eraseSharedCookie('si_user_profile');
    }
  }, [userProfile]);


  // Stacked Toaster warnings array
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = (type: 'success' | 'info' | 'warning' | 'error', text: string) => {
    // 1. Completely disable toast notifications on mobile screens (viewport width < 768px)
    if (window.innerWidth < 768) {
      return;
    }

    const lowerText = text.toLowerCase();

    // 2. Filter desktop notifications
    // Errors and warnings are always shown, except for minor coming-soon warning clicks
    if (type === 'error' || type === 'warning') {
      const blockedWarnings = ['coming soon'];
      if (blockedWarnings.some(keyword => lowerText.includes(keyword))) {
        return;
      }
    } else {
      // For 'success' and 'info' messages, we only allow those matching major event themes:
      const allowedThemes = [
        'generate', 'compress', 'convert', 'resize', 'download', 'upload', 
        'export', 'complete', 'delete', 'signin', 'sign-in', 'sign up', 
        'logged in', 'credits', 'reward', 'plan switched', 'activated',
        'ticket', 'reply', 'notify', 'face auto-detected'
      ];

      const isAllowed = allowedThemes.some(theme => lowerText.includes(theme));

      // Block specific minor/temporary/process-start messages even if they match above themes
      const blockedMinorDetails = [
        'opening', 'focus', 'toggled', 'connecting', 'removed', 
        'imported', 'clipboard', 'brush active', 'preload', 'history purged',
        'reset background', 'gradient applied', 'standard set to', 
        'checklist verified', 'cleared slot', 'collage reset', 'loaded slot'
      ];

      const isBlocked = blockedMinorDetails.some(keyword => lowerText.includes(keyword));

      if (!isAllowed || isBlocked) {
        return;
      }
    }

    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, type, text }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Helper function to update state and browser history path
  const navigate = (path: string) => {
    if (path === '/bulk-image-optimizer') {
      path = '/bulk-image-compressor';
    }
    window.history.pushState(null, '', path);
    // Dispatch custom popstate event so all routing listeners globally sync automatically
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Listen to popstate event to synchronize client-side routes
  useEffect(() => {
    const handlePopState = () => {
      let path = window.location.pathname;
      const isAppSub = window.location.hostname.includes('app.');
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (path === '/dashboard' && isAppSub && !isLocalhost) {
        path = '/';
        window.history.replaceState(null, '', path);
      }
      
      setCurrentPath(path);

      // Actual dashboard tool paths (NOT including '/')
      const toolPaths = [
        '/dashboard', '/compress-image', '/bulk-image-compressor', '/ai-image-generator',
        '/image-resizer', '/image-format-converter', '/stock-library',
        '/account', '/settings', '/referral', '/history', '/donate', '/collage-maker', '/passport-photo-maker'
      ];
      
      let hasProfile = false;
      try {
        const stored = localStorage.getItem('si_user_profile');
        if (stored) hasProfile = true;
      } catch {}

      const specialPaths = [
        '/privacy-policy', '/terms-of-service', '/disclaimer', '/cookie-policy', '/faq',
        '/sitemap', '/about', '/about-us', '/blog', '/contact', '/contact-us', '/tools', '/dashboard-preview',
        '/background-remover', '/image-upscaler', '/watermark-remover', '/image-to-pdf',
        '/pdf-to-image', '/compress-gif', '/image-color-picker', '/exif-remover',
        '/pricing'
      ];
      const isSpecial = (specialPaths.includes(path) && !(hasProfile && (path === '/pricing' || path === '/about' || path === '/about-us'))) || (!hasProfile && !isAppSub && toolPaths.includes(path) && path !== '/dashboard');

      if (path === ADMIN_PATH) {
        setCurrentView('admin');
        return;
      }

      if (isSpecial) {
        setCurrentView('special');
        return;
      }

      if (path === '/') {
        if (isAppSub && userProfile) {
          setCurrentView('app');
        } else {
          setCurrentView('landing');
        }
        return;
      }

      // Treat path as a tool path ONLY if user has a profile/authenticated
      const isToolPath = (toolPaths.includes(path) || ((path === '/pricing' || path === '/about' || path === '/about-us') && hasProfile)) && hasProfile;

      // Handle tool paths
      if (isToolPath) {
        // In production on naked domain, redirect to app subdomain
        if (!isAppSub && !isLocalhost) {
          window.location.href = `https://app.smartimagify.com${path}`;
          return;
        }
        setCurrentView('app');
      } else {
        setCurrentView('landing');
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Run sync on mount to ensure immediate state loading matches URL
    handlePopState();

    return () => window.removeEventListener('popstate', handlePopState);
  }, [userProfile]);

  const syncUserProfileFromDB = async (userId: string) => {
    try {
      const { data: dbProfile, error: dbError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (dbError) {
        console.warn("Could not sync user profile from DB:", dbError);
        return;
      }

      if (!dbProfile) {
        // User profile has been deleted by admin from database! Clean up session and log out
        setUserProfile(null);
        localStorage.removeItem('si_user_profile');
        try { supabase.auth.signOut(); } catch {}
        navigate('/');
        return;
      }

      // Check if we have guest smashed credits to claim
      let smashedCredits = 0;
      try {
        const storedSmashed = localStorage.getItem('si_smashed_credits');
        if (storedSmashed) {
          smashedCredits = parseInt(storedSmashed, 10);
        }
      } catch {}

      let currentBalance = dbProfile.credit_balance ?? 20;

      if (smashedCredits > 0) {
        // Clear local storage immediately to prevent double claim
        try {
          localStorage.removeItem('si_smashed_credits');
        } catch {}

        // Add smashed credits to current balance
        const newBalance = currentBalance + smashedCredits;
        
        // Update database
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ credit_balance: newBalance })
          .eq('id', userId);

        if (!updateError) {
          currentBalance = newBalance;
          addToast('success', `🎉 Welcome! +${smashedCredits} bubble credits have been added to your profile!`);
          localStorage.setItem('si_bubble_credits_claimed', 'true');
          localStorage.setItem('si_bubble_credits_amount', '100');
          window.dispatchEvent(new CustomEvent('si_bubble_credits_fully_claimed'));
          
          // Log smashed credits transaction
          await supabase.from('credit_transactions').insert({
            user_id: userId,
            amount: smashedCredits,
            reason: 'Bubble smash bonus credits'
          });
        } else {
          console.warn("Failed to update credit balance with smashed credits:", updateError);
        }
      }

      // Check and log the sign up bonus of +20 if it is missing in the ledger
      try {
        const { data: txs, error: txError } = await supabase
          .from('credit_transactions')
          .select('id')
          .eq('user_id', userId)
          .eq('reason', 'Sign Up Bonus')
          .limit(1);

        if (!txError && (!txs || txs.length === 0)) {
          await supabase.from('credit_transactions').insert({
            user_id: userId,
            amount: 20,
            reason: 'Sign Up Bonus'
          });
        }
      } catch (err) {
        console.warn("Failed checking signup bonus log:", err);
      }

      const actualPlan = dbProfile.plan || 'free';
      const isPaid = actualPlan !== 'free';
      localStorage.setItem('si_active_plan', isPaid ? 'pro' : 'free');
      localStorage.setItem('si_active_plan_tier', actualPlan);
      
      setUserProfile(prev => {
        if (!prev) return null;
        const updated = {
          ...prev,
          id: dbProfile.id,
          credit_balance: currentBalance,
          plan: dbProfile.plan || 'free',
          is_blocked: dbProfile.is_blocked || false,
          last_daily_claim: dbProfile.last_daily_claim,
          is_admin: dbProfile.is_admin || dbProfile.role === 'admin' || false
        };
        localStorage.setItem('si_user_profile', JSON.stringify(updated));
        return updated;
      });
    } catch (e) {
      console.warn("Could not sync user profile from DB:", e);
    }
  };

  const syncGuestProfileFromDB = async () => {
    try {
      const stored = localStorage.getItem('si_user_profile');
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (parsed.email !== 'guest@smartimagify.com') return;

      const fingerprint = await getDeviceFingerprint();
      const { data, error } = await supabase
        .from('guest_devices')
        .select('*')
        .eq('device_id', fingerprint)
        .maybeSingle();

      if (error) {
        console.warn("Could not sync guest profile from DB:", error);
        return;
      }

      if (data) {
        if (data.credit_balance <= 0) {
          addToast('warning', 'Your free guest credits have expired on this device. Please sign up to continue!');
          setUserProfile(null);
          localStorage.removeItem('si_user_profile');
          navigate('/');
          return;
        }

        const updated = {
          ...parsed,
          credit_balance: data.credit_balance
        };
        setUserProfile(updated);
        localStorage.setItem('si_user_profile', JSON.stringify(updated));
      } else {
        await supabase
          .from('guest_devices')
          .insert({
            device_id: fingerprint,
            credit_balance: parsed.credit_balance ?? 20
          });
      }
    } catch (err) {
      console.warn("Error in syncGuestProfileFromDB:", err);
    }
  };

  // Sync session and capture OAuth callbacks from Supabase natively
  useEffect(() => {
    // 1. Restore local session details on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && session.user) {
        const metadata = session.user.user_metadata || {};
        const profile: UserProfile = {
          id: session.user.id,
          name: metadata.full_name || metadata.name || session.user.email?.split('@')[0] || 'Authenticated User',
          email: session.user.email || '',
          dp: metadata.avatar_url || '',
          country: metadata.country || 'Pakistan',
          phone: metadata.phone || '',
          created_at: session.user.created_at
        };
        setUserProfile(profile);
        localStorage.setItem('si_user_profile', JSON.stringify(profile));
        syncUserProfileFromDB(session.user.id);
      } else {
        // Clear stale local profile if session expired and it was an email profile
        try {
          const stored = localStorage.getItem('si_user_profile');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.email !== 'guest@smartimagify.com') {
              localStorage.removeItem('si_user_profile');
              setUserProfile(null);
            } else {
              syncGuestProfileFromDB();
            }
          }
        } catch {}
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session && session.user) {
        try {
          localStorage.removeItem('si_guest_single_compress_count');
          localStorage.removeItem('si_guest_task_count');
        } catch {}
        
        const metadata = session.user.user_metadata || {};
        const profile: UserProfile = {
          id: session.user.id,
          name: metadata.full_name || metadata.name || session.user.email?.split('@')[0] || 'Authenticated User',
          email: session.user.email || '',
          dp: metadata.avatar_url || '',
          country: metadata.country || 'Pakistan',
          phone: metadata.phone || '',
          created_at: session.user.created_at
        };
        
        setUserProfile(profile);
        localStorage.setItem('si_user_profile', JSON.stringify(profile));
        syncUserProfileFromDB(session.user.id);
        setShowSignIn(false);

        // Auto-navigate to dashboard on successful OAuth redirect
        const isToolPath = [
          '/dashboard', '/compress-image', '/bulk-image-compressor', '/ai-image-generator',
          '/image-resizer', '/image-format-converter', '/stock-library', '/about-us', '/pricing',
          '/account', '/settings', '/referral', '/history', '/donate', '/collage-maker', '/passport-photo-maker'
        ].includes(window.location.pathname);

        if (window.location.pathname === '/' || window.location.pathname === '') {
          setCurrentView('app');
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const isAppSub = window.location.hostname.includes('app.');
          navigate(!isLocalhost && isAppSub ? '/' : '/dashboard');
        } else if (isToolPath) {
          setCurrentView('app');
        }
      } else if (event === 'SIGNED_OUT') {
        setUserProfile(null);
        try {
          localStorage.removeItem('si_user_profile');
        } catch {}
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Real-time profile sync — instantly reflects admin changes (credits, plan, blocked)
  useEffect(() => {
    if (!userProfile?.id) return;

    const channel = supabase
      .channel(`profile-sync-${userProfile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userProfile.id}`
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setUserProfile(null);
            localStorage.removeItem('si_user_profile');
            try { supabase.auth.signOut(); } catch {}
            setCurrentView('landing');
            navigate('/');
            return;
          }

          const updated = payload.new as any;

          // Sync credit_balance, plan, and block status in real-time
          setUserProfile(prev => {
            if (!prev) return null;
            const merged = {
              ...prev,
              credit_balance: updated.credit_balance ?? prev.credit_balance,
              plan: updated.plan || prev.plan,
              is_blocked: updated.is_blocked ?? prev.is_blocked,
              last_daily_claim: updated.last_daily_claim ?? prev.last_daily_claim
            };
            localStorage.setItem('si_user_profile', JSON.stringify(merged));
            localStorage.setItem('si_active_plan', merged.plan || 'free');
            return merged;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userProfile?.id]);

  // Intercept all clean relative links globally to avoid browser page reload
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');
      if (anchor) {
        const href = anchor.getAttribute('href');
        // Only intercept clean paths starting with "/" and not hashes or external domains
        if (href && href.startsWith('/') && !href.startsWith('//')) {
          e.preventDefault();
          
          const accountPaths = ['/dashboard', '/account', '/settings', '/referral', '/history'];
          
          if (accountPaths.includes(href) && !userProfile) {
            setPendingTabPath(href === '/dashboard' ? '/' : href);
            setShowSignIn(true);
            return;
          }
          
          navigate(href);
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, [userProfile]);

  // Update dynamic SEO configurations whenever path changes
  useEffect(() => {
    injectSEO(currentPath);
  }, [currentPath]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPath]);

  const routeToLauncher = () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isAppSubdomain = window.location.hostname.includes('app.');

    if (!userProfile) {
      setPendingTabPath(isLocalhost ? '/dashboard' : '/');
      setShowSignIn(true);
    } else {
      if (!isLocalhost && !isAppSubdomain) {
        window.location.href = 'https://app.smartimagify.com/';
      } else {
        setCurrentView('app');
        navigate(isLocalhost ? '/dashboard' : '/');
      }
    }
  };

  // Handle pricing plan selection — checks sign-in, then celebrates
  const handlePlanSelect = (planName: string) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (!userProfile) {
      setPendingPlanActivation(planName);
      setPendingTabPath('/');
      setShowSignIn(true);
    } else {
      try {
        localStorage.setItem('si_confirm_subscription', planName);
        localStorage.setItem('si_initial_tab', 'pricing');
      } catch {}
      setCurrentView('app');
      navigate(isLocalhost ? '/dashboard' : '/');
    }
  };

  const activatePlan = async (planName: string, customProfile?: UserProfile | null) => {
    const activeUser = customProfile || userProfile;
    const getPlanCredits = (name: string) => {
      switch (name.toLowerCase()) {
        case 'basic': return 50;
        case 'starter': return 250;
        case 'plus': return 300;
        case 'pro': return 1000;
        case 'ultra': return 1000;
        case 'business': return 3000;
        case 'pro-team': return 10000;
        default: return 20; // free
      }
    };
    const credits = getPlanCredits(planName);

    try {
      localStorage.setItem('si_active_plan', planName === 'free' ? 'free' : 'pro');
      localStorage.setItem('si_active_plan_tier', planName.toLowerCase());
    } catch {}

    // Sync to Supabase
    if (activeUser && !activeUser.email.includes('guest')) {
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ 
            plan: planName.toLowerCase(),
            credit_balance: credits 
          })
          .eq('id', activeUser.id);

        if (!profileError) {
          // Log plan upgrade credits to ledger
          await supabase.from('credit_transactions').insert({
            user_id: activeUser.id,
            amount: credits,
            reason: `Plan Upgrade to ${planName.toUpperCase()}`
          });
        }
      } catch (err) {
        console.warn("Failed syncing upgraded plan to Supabase:", err);
      }
    }

    // Update local state profile
    if (activeUser) {
      const updated = {
        ...activeUser,
        plan: planName.toLowerCase(),
        credit_balance: credits
      };
      setUserProfile(updated);
      try {
        localStorage.setItem('si_user_profile', JSON.stringify(updated));
      } catch {}
    }

    const isPremium = planName !== 'free';
    if (isPremium) {
      setShowCelebration(true);
    } else {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      navigate(isLocalhost ? '/dashboard' : '/');
    }
  };

  const [isDashboardLoading, setIsDashboardLoading] = useState(false);

  // Sign-in success callback
  const handleSignInSuccess = (user: UserProfile) => {
    try {
      localStorage.removeItem('si_guest_single_compress_count');
      localStorage.removeItem('si_guest_task_count');
    } catch {}

    const isGuest = user.email === 'guest@smartimagify.com';
    const smashed = localStorage.getItem('si_smashed_credits');
    const smashedNum = smashed ? (parseInt(smashed, 10) || 0) : 0;

    if (isGuest) {
      const dbCredits = user.credit_balance ?? 20;
      user.credit_balance = dbCredits + smashedNum;
      user.plan = 'free';
      try {
        localStorage.removeItem('si_smashed_credits');
        localStorage.setItem('si_bubble_credits_claimed', 'true');
        localStorage.setItem('si_bubble_credits_amount', smashedNum.toString());
        
        if (smashedNum > 0) {
          (async () => {
            try {
              const fingerprint = await getDeviceFingerprint();
              await supabase
                .from('guest_devices')
                .update({ credit_balance: dbCredits + smashedNum })
                .eq('device_id', fingerprint);
            } catch (err) {
              console.error('Error adding smashed credits for guest device:', err);
            }
          })();
        }
      } catch {}

      setUserProfile(user);
      localStorage.setItem('si_user_profile', JSON.stringify(user));
      setShowSignIn(false);

      // Trigger premium loading screen
      setIsDashboardLoading(true);

      // Delay routing for 1.5 seconds to allow background loading feel
      setTimeout(() => {
        setIsDashboardLoading(false);
        addToast('success', `Welcome, Guest User! 🎉 You have ${user.credit_balance} credits.`);
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isAppSub = window.location.hostname.includes('app.');
        navigate(!isLocalhost && isAppSub ? '/' : '/dashboard');
      }, 1500);
      return;
    }

    // Check if they accumulated any smashed credits on landing page
    if (smashed) {
      localStorage.removeItem('si_smashed_credits');
      (async () => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('credit_balance')
            .eq('id', user.id)
            .maybeSingle();

          let currentCredits = 20; // Default sign up credits
          if (!error && data) {
            currentCredits = data.credit_balance ?? 20;
          }

          // Update profile with signup credits + smashed credits!
          await supabase
            .from('profiles')
            .update({ credit_balance: currentCredits + smashedNum })
            .eq('id', user.id);

          // Log transaction for smashed credits
          await supabase.from('credit_transactions').insert({
            user_id: user.id,
            amount: smashedNum,
            reason: 'Bubble smash bonus credits'
          });

          localStorage.setItem('si_bubble_credits_claimed', 'true');
          localStorage.setItem('si_bubble_credits_amount', smashedNum.toString());
          window.dispatchEvent(new CustomEvent('si_bubble_credits_fully_claimed'));

          syncUserProfileFromDB(user.id);
        } catch (err) {
          console.warn("Failed transferring smashed credits to user profile:", err);
        }
      })();
    }

    setUserProfile(user);
    setShowSignIn(false);
    
    // Trigger premium loading screen
    setIsDashboardLoading(true);
    
    // Delay routing for 1.5 seconds to allow background loading feel
    setTimeout(() => {
      const pendingPlan = localStorage.getItem('si_pending_plan_activation') || pendingPlanActivation;
      let pendingTab = localStorage.getItem('si_pending_tab') || pendingTabPath;
      if (pendingTab === '/dashboard') {
        pendingTab = '/';
      }
      setIsDashboardLoading(false);
      addToast('success', `Welcome, ${user.name}! 🎉`);
      
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const homePath = isLocalhost ? '/dashboard' : '/';
      
      if (pendingPlan) {
        localStorage.removeItem('si_pending_plan_activation');
        localStorage.removeItem('si_pending_tab');
        setPendingPlanActivation(null);
        setPendingTabPath(null);
        
        try {
          localStorage.setItem('si_confirm_subscription', pendingPlan);
          localStorage.setItem('si_initial_tab', 'pricing');
        } catch {}

        setCurrentView('app');
        // Handle production subdomain redirect or local navigate
        const isMainDomain = !isLocalhost && !window.location.hostname.includes('app.');
        if (isMainDomain) {
          window.location.href = 'https://app.smartimagify.com/';
        } else {
          navigate(homePath);
        }
      } else if (pendingTab && pendingTab !== '/') {
        localStorage.removeItem('si_pending_tab');
        try { localStorage.setItem('si_initial_tab', pendingTab); } catch {}
        setCurrentView('app');
        const isMainDomain = !isLocalhost && !window.location.hostname.includes('app.');
        if (isMainDomain) {
          window.location.href = `https://app.smartimagify.com${pendingTab}`;
        } else {
          navigate(pendingTab);
        }
      } else {
        // After sign-in on main domain, redirect to app subdomain
        const isMainDomain = !isLocalhost && !window.location.hostname.includes('app.');
        if (isMainDomain) {
          window.location.href = 'https://app.smartimagify.com/';
        } else {
          navigate(homePath);
        }
      }
    }, 1500);
  };

  const handleUpdateProfile = async (updated: Partial<UserProfile>) => {
    if (!userProfile) return;
    
    // 1. Optimistically update local state & local storage for instant dynamic UI
    const newProfile = { ...userProfile, ...updated };
    setUserProfile(newProfile);
    try {
      localStorage.setItem('si_user_profile', JSON.stringify(newProfile));
    } catch {}

    // Smart Supabase Cloud Sync
    const isGuest = userProfile.email === 'guest@smartimagify.com';
    const isPlaceholder = !import.meta.env.VITE_SUPABASE_URL || 
                          import.meta.env.VITE_SUPABASE_URL.includes('placeholder');
    
    if (isGuest || isPlaceholder) return;

    try {
      let finalAvatarUrl = updated.dp;
      
      // If the profile picture is updated and contains new base64 data, upload to Supabase Storage!
      if (updated.dp && updated.dp.startsWith('data:image/')) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Convert base64 data to Blob
          const response = await fetch(updated.dp);
          const blob = await response.blob();
          const fileExt = blob.type.split('/')[1] || 'png';
          const fileName = `${user.id}-${Date.now()}.${fileExt}`;
          const filePath = `public/${fileName}`;

          // Create avatars bucket if it doesn't exist (fails gracefully if already exists)
          try {
            await supabase.storage.createBucket('avatars', { public: true });
          } catch {}

          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, blob, { upsert: true });

          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage
              .from('avatars')
              .getPublicUrl(filePath);
            finalAvatarUrl = publicUrl;
          }
        }
      }

      // Update auth user metadata
      const { error } = await supabase.auth.updateUser({
        data: {
          firstName: updated.firstName,
          lastName: updated.lastName,
          full_name: updated.name,
          phone: updated.phone,
          country: updated.country,
          avatar_url: finalAvatarUrl
        }
      });

      // Also sync to public.profiles table for persistent storage
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('profiles').upsert({
          id: user.id,
          full_name: updated.name || newProfile.name,
          email: newProfile.email,
          phone: updated.phone || newProfile.phone,
          country: updated.country || newProfile.country,
          avatar_url: finalAvatarUrl || newProfile.dp
        }, { onConflict: 'id' });
      }

      if (!error && finalAvatarUrl && finalAvatarUrl !== updated.dp) {
        // Sync the public storage URL back to local profile
        const syncedProfile = { ...newProfile, dp: finalAvatarUrl };
        setUserProfile(syncedProfile);
        localStorage.setItem('si_user_profile', JSON.stringify(syncedProfile));
      }
    } catch (err) {
      console.warn("Supabase profile sync failed:", err);
    }
  };

  // Celebration close — route to root dashboard
  const handleCelebrationClose = () => {
    setShowCelebration(false);
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    navigate(isLocalhost ? '/dashboard' : '/');
  };

  // Sign out handler — clears ALL app data for a completely fresh state
  const handleSignOut = () => {
    // Destroy active Supabase database session
    try {
      supabase.auth.signOut();
    } catch {}

    const isGuest = userProfile?.email === 'guest@smartimagify.com';

    // Clear all SmartImagify related localStorage keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => {
      try { 
        // DO NOT remove pending plan activation or pending sign-in triggers!
        if (key !== 'si_pending_plan_activation' && key !== 'si_pending_tab' && key !== 'si_trigger_signin') {
          localStorage.removeItem(key);
        }
      } catch {}
    });
    
    // Clear sessionStorage too
    try { sessionStorage.clear(); } catch {}
    
    // Reset user profile state
    setUserProfile(null);
    eraseSharedCookie('si_user_profile');
    
    // Set view back to landing to prevent rendering dashboard with null profile!
    setCurrentView('landing');
    navigate('/');
    
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalhost) {
      window.location.href = 'https://smartimagify.com';
    } else {
      if (isGuest) {
        setShowSignIn(true);
      }
    }
  };

  // Auto-open Sign-In modal if URL has trigger/mode or if Guest user was redirected to sign-up
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlMode = params.get('mode');
      const urlTrigger = params.get('trigger');
      const urlPlan = params.get('plan');
      
      if (urlMode === 'signin' || urlMode === 'signup' || urlTrigger === 'signin') {
        const newurl = window.location.protocol + "//" + window.location.host + window.location.pathname;
        window.history.pushState({ path: newurl }, '', newurl);
        
        if (urlPlan) {
          setPendingPlanActivation(urlPlan);
          localStorage.setItem('si_pending_plan_activation', urlPlan);
        }
        setSignInMode(urlMode === 'signup' ? 'signup' : 'signin');
        setShowSignIn(true);
        return;
      }

      const trigger = localStorage.getItem('si_trigger_signin');
      const pendingPlan = localStorage.getItem('si_pending_plan_activation');
      if (trigger === 'true' || pendingPlan) {
        localStorage.removeItem('si_trigger_signin');
        setShowSignIn(true);
        if (pendingPlan) {
          setPendingTabPath('/');
        }
      }
    } catch {}
  }, []);

  // Escape key closure of any modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowShortcuts(false);
        setShowChangelog(false);
        setShowHelp(false);
        setShowSignIn(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <>
      {userProfile?.is_blocked ? (
        <div className="fixed inset-0 bg-[#121214] flex items-center justify-center z-[99999] p-4 font-sans">
          <div className="w-full max-w-sm bg-[#161619] border border-white/10 rounded-xl p-6 text-center space-y-4 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mx-auto">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="space-y-1">
              <h3 className="text-[13px] font-bold text-white uppercase tracking-wider">Account Blocked</h3>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Your account has been suspended by the administrator. Please contact support.
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full py-2 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
            >
              Sign Out
            </button>
          </div>
        </div>
      ) : (
        <>
          {(currentView === 'landing' || currentView === 'special') && (
            <LandingPage 
              onLaunchTool={routeToLauncher} 
              onPlanSelect={handlePlanSelect}
              onSignIn={(targetPath?: string, mode?: 'signin' | 'signup') => {
                setPendingTabPath(targetPath || '/');
                setSignInMode(mode || 'signin');
                setShowSignIn(true);
              }}
              userProfile={userProfile}
              onSignOut={handleSignOut}
              currentView={currentView}
              theme={theme}
              toggleTheme={toggleTheme}
              specialContent={
                currentView === 'special' ? (
                  <Suspense fallback={<LoadingFallback />}>
                    <SpecialPages 
                      path={currentPath}
                      theme={theme}
                      onBackToHome={() => {
                        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                        if (!isLocalhost) {
                          window.location.href = 'https://smartimagify.com';
                        } else {
                          navigate('/');
                        }
                      }}
                      addToast={addToast}
                      onSignIn={(targetPath?: string, mode?: 'signin' | 'signup') => {
                        setPendingTabPath(targetPath || '/');
                        setSignInMode(mode || 'signin');
                        setShowSignIn(true);
                      }}
                      onLaunchTool={routeToLauncher}
                      onPlanSelect={handlePlanSelect}
                    />
                  </Suspense>
                ) : null
              }
            />
          )}

          {currentView === 'app' && (
            <Suspense fallback={<LoadingFallback />}>
              <DashboardTool 
                onBackToHome={() => {
                  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                  if (!isLocalhost) {
                    window.location.href = 'https://smartimagify.com';
                  } else {
                    navigate('/');
                  }
                }}
                addToast={addToast} 
                showShortcuts={showShortcuts}
                setShowShortcuts={setShowShortcuts}
                setShowChangelog={setShowChangelog}
                setShowHelp={setShowHelp}
                userProfile={userProfile}
                onSignOut={handleSignOut}
                onUpdateProfile={handleUpdateProfile}
                theme={theme}
                toggleTheme={toggleTheme}
              />
            </Suspense>
          )}

          {currentView === 'admin' && (
            <Suspense fallback={<LoadingFallback />}>
              <AdminPortal 
                addToast={addToast}
                onBackToHome={() => {
                  navigate('/');
                }}
                theme={theme}
                toggleTheme={toggleTheme}
              />
            </Suspense>
          )}
        </>
      )}

      {/* Stacked float Toaster list */}
      <Toasts toasts={toasts} removeToast={removeToast} />

      {/* Global Modals container panels */}
      <Modals 
        showShortcuts={showShortcuts}
        setShowShortcuts={setShowShortcuts}
        showChangelog={showChangelog}
        setShowChangelog={setShowChangelog}
        showHelp={showHelp}
        setShowHelp={setShowHelp}
      />

      {/* Full-Screen Sign-In Page */}
      <Suspense fallback={null}>
        <SignInModal 
          isOpen={showSignIn} 
          onClose={() => { setShowSignIn(false); setPendingPlanActivation(null); setPendingTabPath(null); }}
          onSuccess={handleSignInSuccess}
          initialMode={signInMode}
          theme={theme}
        />
      </Suspense>

      {/* Celebration Modal */}
      <Suspense fallback={null}>
        <CelebrationModal 
          isOpen={showCelebration} 
          onClose={handleCelebrationClose}
        />
      </Suspense>

      {/* Premium Sign-In Loader */}
      {isDashboardLoading && <DashboardLoader theme={theme} />}
    </>
  );
}
